import React, { useState } from 'react';
import { CheckCircle2, Circle, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../context/LanguageContext';

const tzs = (n) => `TZS ${Math.round(n || 0).toLocaleString('en-US')}`;

const Steps = ({ r, t }) => {
  const paid = (r.payments || []).some((p) => p.status === 'confirmed');
  const steps = [
    [r.source === 'linked_existing' ? t('WHI account linked', 'Akaunti ya WHI imeunganishwa')
      : ['pending_whi_registration', 'needs_info'].includes(r.status) ? t('Sent to WHI', 'Imetumwa WHI') : t('Registered with WHI', 'Imesajiliwa WHI'), true],
    [t('Account & Control Number issued', 'Akaunti na Control Number zimetolewa'), !!r.control_number],
    [t('You paid via GePG', 'Umelipa kupitia GePG'), (r.payments || []).length > 0],
    [t('WHI confirmed units', 'WHI wamethibitisha vipande'), paid],
  ];
  return (
    <ol className="grid gap-2 sm:grid-cols-4" data-testid="faida-steps">
      {steps.map(([label, done], i) => (
        <li key={label} className={`flex items-center gap-2 rounded-xl border p-3 text-xs ${done ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-border text-muted-foreground'}`} data-testid={`faida-step-${i}`}>
          {done ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Circle className="h-4 w-4 shrink-0" />}{label}
        </li>
      ))}
    </ol>
  );
};

const Numbers = ({ r, t }) => {
  const copy = (v) => navigator.clipboard?.writeText(v);
  return (
    <div className="grid gap-3 rounded-2xl border border-amber-300/60 bg-amber-50/60 p-4 sm:grid-cols-2" data-testid="faida-numbers">
      <div><p className="text-xs text-muted-foreground">{t('Faida Fund account', 'Akaunti ya Faida Fund')}</p>
        <p className="font-mono text-lg font-bold" data-testid="faida-account-number">{r.account_number}</p></div>
      <div><p className="text-xs text-muted-foreground">{t('GePG Control Number', 'Control Number ya GePG')}</p>
        <button onClick={() => copy(r.control_number)} className="flex items-center gap-2 font-mono text-lg font-bold" data-testid="faida-control-number">
          {r.control_number}<Copy className="h-4 w-4 text-muted-foreground" /></button></div>
      <a href={`/pay/faida?ctrl=${r.control_number}&amt=${r.desired_amount_tzs || 10000}`} className="sm:col-span-2" data-testid="faida-pay-link">
        <Button className="w-full">{t('How to pay with M-Pesa / Mixx / Airtel', 'Jinsi ya kulipa kwa M-Pesa / Mixx / Airtel')}<ExternalLink className="ml-2 h-4 w-4" /></Button>
      </a>
      <p className="text-xs text-muted-foreground sm:col-span-2">{t('Money goes directly to WHI through the Government e-Payment Gateway (GePG).', 'Pesa inaenda moja kwa moja WHI kupitia GePG ya Serikali.')}</p>
    </div>
  );
};

const ReportPayment = ({ t, onReport }) => {
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await onReport({ amount_tzs: Number(amount), reference: ref })) { setAmount(''); setRef(''); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-2xl border border-border p-4" data-testid="faida-report-form">
      <p className="text-sm font-semibold">{t('Already paid? Tell WHI so they can confirm your units', 'Umeshalipa? Waambie WHI wathibitishe vipande vyako')}</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input type="number" min={5000} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('Amount paid (TZS)', 'Kiasi ulicholipa (TZS)')} required data-testid="faida-report-amount" />
        <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t('Receipt / transaction ID', 'Namba ya muamala')} required data-testid="faida-report-ref" />
        <Button type="submit" disabled={busy} data-testid="faida-report-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('I have paid', 'Nimelipa')}</Button>
      </div>
    </form>
  );
};

const DELIVERY = { opened: ['Opened by WHI', 'Imefunguliwa na WHI'], delivered: ['Delivered', 'Imefika'], sent: ['Sent', 'Imetumwa'],
  bounced: ['Not delivered — our team is following up', 'Haikufika — timu yetu inafuatilia'], failed: ['Sending failed — our team is following up', 'Imeshindikana — timu yetu inafuatilia'] };

const Delivery = ({ d, t }) => {
  if (!d?.sent_at) return null;
  const when = new Date(d.sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const label = DELIVERY[d.delivery] || DELIVERY.sent;
  return (
    <p className="text-xs text-muted-foreground" data-testid="faida-delivery">
      {t('Sent to', 'Imetumwa kwa')} {d.recipients.join(' · ')} {t('on', 'tarehe')} {when} · <b data-testid="faida-delivery-state">{t(...label)}</b>
      {d.reminders > 0 && <> · {t(`${d.reminders} reminder${d.reminders > 1 ? 's' : ''} sent`, `vikumbusho ${d.reminders} vimetumwa`)}</>}
    </p>
  );
};

export const FaidaTracker = ({ r, onReport }) => {
  const { t } = useLanguage();
  return (
    <div className="space-y-4" data-testid="faida-tracker">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono text-xs text-muted-foreground" data-testid="faida-lead-id">{r.lead_id}</span>
        <Badge variant="outline" data-testid="faida-status">{r.status.replace(/_/g, ' ')}</Badge>
      </div>
      <Steps r={r} t={t} />
      <Delivery d={r.whi_delivery} t={t} />
      {!r.control_number && r.status !== 'whi_registered' && (
        <p className="rounded-xl bg-sky-50 p-4 text-sm text-sky-800" data-testid="faida-waiting">
          {t('WHI is opening your account. You will get your Account and Control Number here, on WhatsApp and by email.',
            'WHI wanakufungulia akaunti. Utapokea Akaunti na Control Number hapa, WhatsApp na barua pepe.')}
        </p>
      )}
      {r.control_number && <Numbers r={r} t={t} />}
      {r.control_number && <ReportPayment t={t} onReport={onReport} />}
      {!!r.payments?.length && (
        <ul className="divide-y divide-border rounded-2xl border border-border" data-testid="faida-payments">
          {r.payments.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm" data-testid={`faida-payment-${p.id}`}>
              <span>{tzs(p.amount_confirmed_tzs || p.amount_tzs)} · <span className="font-mono text-xs">{p.reference}</span></span>
              <Badge variant="outline" className={p.status === 'confirmed' ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'} data-testid={`faida-payment-status-${p.id}`}>
                {p.status === 'confirmed' ? t(`${p.units} units confirmed`, `vipande ${p.units} vimethibitishwa`) : t('Waiting for WHI', 'Inasubiri WHI')}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
